import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faDown, faUp } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { InningHalf } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineM, LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { NamedClubAvatar } from '@sorare/core/src/components/club/NamedClubAvatar';
import { TeamAvatar } from '@sorare/core/src/components/club/TeamAvatar';
import { LiveDot } from '@sorare/core/src/components/live/LiveDot';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { getShortPlayerName } from '@sorare/core/src/lib/players';

import { DomesticLeagueRanking } from '@sorare/marketplace/src/shared/game/DomesticLeagueRanking';
import {
  isGameFinished,
  isGameLive,
} from '@sorare/marketplace/src/shared/game/lib';
import { HandInfoChip } from '@sorare/marketplace/src/shared/player/HandInfoChip';

import { PlayerAvatar } from '#src/components/player/PlayerAvatar/index.tsx';
import { GamesStanding } from '#src/components/team/GamesStanding/index.tsx';
import { getTeamName } from '#src/lib/so5.ts';

import type {
  GameTeamsMatchup_anyGameInterface,
  GameTeamsMatchup_pollingAnyGameInterface,
} from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 1.5 })`
  width: 100%;
`;
const Matchup = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content minmax(0, 1fr);
  align-items: center;
  justify-items: center;
  gap: var(--unit);
  width: 100%;
  overflow: hidden;

  &.reversed {
    > *:first-child {
      order: 2;
    }
    > *:nth-child(3) {
      order: -1;
    }
  }
`;
const Scores = styled(Horizontal)`
  color: var(--c-nd-600);
  font-weight: normal;
  .reversed & {
    flex-direction: row-reverse;
  }

  .winningOrDraw {
    font-weight: var(--t-bold);
    color: var(--c-white);
  }
`;
const LiveWrapper = styled(Horizontal)`
  border-radius: var(--half-unit);
  padding: var(--half-unit);
  background-color: rgba(var(--c-rgb-red-400), 0.16);
  color: var(--c-red-300);
`;

const MiniPlayerAvatar = styled(PlayerAvatar)`
  width: var(--double-unit);
  height: var(--double-unit);
  background: var(--c-gradient-card-placeholder-common);
  border-radius: 50%;
  border: 1px solid var(--c-nd-50);
`;

type Props = {
  game: GameTeamsMatchup_anyGameInterface;
  displayDay?: boolean;
  children?: React.ReactNode;
};
export const GameTeamsMatchup = ({ game, displayDay, children }: Props) => {
  const homeTeam = {
    name: getTeamName(game.homeTeam),
    avatar: <TeamAvatar team={game.homeTeam} />,
  };
  const awayTeam = {
    name: getTeamName(game.awayTeam),
    avatar: <TeamAvatar team={game?.awayTeam} />,
  };

  const isBaseballGame = isType(game, 'GameOfBaseball');
  const isBasketballGame = isType(game, 'GameOfBasketball');
  const isUsSportsGame = isBaseballGame || isBasketballGame;

  const live = isGameLive(game.status);
  const finished = isGameFinished(game.status);
  const isHomeTeamWinning = game.homeScore > game.awayScore;
  const draw = game.homeScore === game.awayScore;

  const [awayPitcher, homePitcher] = isBaseballGame
    ? [game.awayProbablePitcher, game.homeProbablePitcher]
    : [];

  return (
    <Root gap={0}>
      <Matchup className={classNames({ reversed: isUsSportsGame })}>
        <Vertical gap={0.5} center>
          <LabelL as="div">
            <NamedClubAvatar team={homeTeam} bold />
          </LabelL>
          {!!game.homeTeam && (
            <>
              {isUsSportsGame ? (
                <GamesStanding team={game.homeTeam} color="var(--c-nd-600)" />
              ) : (
                <LabelS as="span" color="var(--c-nd-600)">
                  <DomesticLeagueRanking game={game} team={game.homeTeam} />
                </LabelS>
              )}
            </>
          )}
          {!!isBaseballGame && !!homePitcher && (
            <Horizontal gap={0.5}>
              <Horizontal>
                <MiniPlayerAvatar player={homePitcher} />
                <LabelS
                  as="span"
                  className="whitespace-nowrap"
                  color="var(--c-nd-600)"
                >
                  {getShortPlayerName(homePitcher.displayName)}
                </LabelS>
              </Horizontal>
              <HandInfoChip position="pitching" player={homePitcher} />
            </Horizontal>
          )}
        </Vertical>
        <Vertical center gap={0.5}>
          <HeadlineM as="span">
            {live || finished ? (
              <Scores gap={0.5}>
                <span
                  className={classNames({
                    winningOrDraw: isHomeTeamWinning || draw,
                  })}
                >
                  {game.homeScore}
                </span>
                <span>-</span>
                <span
                  className={classNames({
                    winningOrDraw: !isHomeTeamWinning || draw,
                  })}
                >
                  {game.awayScore}
                </span>
              </Scores>
            ) : (
              <FormattedDate
                value={game.date}
                hour="numeric"
                minute="numeric"
              />
            )}
            {!!live && (
              <LiveWrapper center>
                <LabelS as="span" bold>
                  <Horizontal gap={0.5}>
                    {isType(game, 'Game') && (
                      <>
                        <LiveDot animate size="sm" />
                        {game.minute}&apos;
                      </>
                    )}
                    {isType(game, 'GameOfBaseball') && (
                      <>
                        <FontAwesomeIcon
                          icon={
                            game.inning.half === InningHalf.TOP ? faUp : faDown
                          }
                          size="sm"
                        />
                        <FormattedMessage
                          {...glossary.ordinal}
                          values={{
                            ordinal: game.inning.number,
                          }}
                        />
                      </>
                    )}
                    {isType(game, 'GameOfBasketball') && (
                      <>
                        <LiveDot animate size="sm" />
                        {game.isHalftime ? (
                          <FormattedMessage
                            id="Game.status.halftime"
                            defaultMessage="Half"
                          />
                        ) : (
                          <FormattedMessage
                            {...glossary.ordinal}
                            values={{ ordinal: game.quarter }}
                          />
                        )}
                      </>
                    )}
                  </Horizontal>
                </LabelS>
              </LiveWrapper>
            )}
          </HeadlineM>
          {!!displayDay && (
            <Chip>
              <FormattedDate value={game.date} month="short" day="2-digit" />
            </Chip>
          )}
        </Vertical>
        <Vertical gap={0.5} center>
          <LabelL as="div">
            <NamedClubAvatar team={awayTeam} bold />
          </LabelL>
          {!!game.awayTeam && (
            <>
              {isUsSportsGame ? (
                <GamesStanding team={game.awayTeam} color="var(--c-nd-600)" />
              ) : (
                <LabelS as="span" color="var(--c-nd-600)">
                  <DomesticLeagueRanking game={game} team={game.awayTeam} />
                </LabelS>
              )}
            </>
          )}
          {!!isBaseballGame && !!awayPitcher && (
            <Horizontal gap={0.5}>
              <Horizontal>
                <MiniPlayerAvatar player={awayPitcher} />
                <LabelS
                  as="span"
                  className="whitespace-nowrap"
                  color="var(--c-nd-600)"
                >
                  {getShortPlayerName(awayPitcher.displayName)}
                </LabelS>
              </Horizontal>
              <HandInfoChip position="pitching" player={awayPitcher} />
            </Horizontal>
          )}
        </Vertical>
      </Matchup>
      {children}
    </Root>
  );
};

GameTeamsMatchup.pollingFragments = {
  anyGameInterface: gql`
    fragment GameTeamsMatchup_pollingAnyGameInterface on AnyGameInterface {
      id
      status: statusTyped
      date
      ... on Game {
        id
        minute
        ...DomesticLeagueRanking_anyGameInterface
      }
      ... on GameOfBasketball {
        id
        isHalftime
        quarter
      }
      homeScore
      awayScore
    }
    ${DomesticLeagueRanking.fragments.anyGameInterface}
  ` as TypedDocumentNode<GameTeamsMatchup_pollingAnyGameInterface>,
};

GameTeamsMatchup.fragments = {
  anyGameInterface: gql`
    fragment GameTeamsMatchup_anyGameInterface on AnyGameInterface {
      id
      status: statusTyped
      date
      homeScore
      awayScore
      ... on Game {
        id
        minute
      }
      ... on GameOfBasketball {
        id
        isHalftime
        quarter
      }
      ... on GameOfBaseball {
        id
        inning {
          half
          number
        }
        homeProbablePitcher {
          slug
          displayName
          ...HandInfoChip_BaseballPlayer
          ...GameplayPlayerAvatar_anyPlayer
        }
        awayProbablePitcher {
          slug
          displayName
          ...HandInfoChip_BaseballPlayer
          ...GameplayPlayerAvatar_anyPlayer
        }
      }
      homeTeam {
        slug
        ...TeamAvatar_team
        ...getTeamName_team
        ...GamesStanding_TeamInterface
        ...DomesticLeagueRanking_teamInterface
      }
      awayTeam {
        slug
        ...TeamAvatar_team
        ...getTeamName_team
        ...GamesStanding_TeamInterface
        ...DomesticLeagueRanking_teamInterface
      }
      ...GameTeamsMatchup_pollingAnyGameInterface
    }
    ${GameTeamsMatchup.pollingFragments.anyGameInterface}
    ${TeamAvatar.fragments.team}
    ${getTeamName.fragments.team}
    ${HandInfoChip.fragments.BaseballPlayer}
    ${PlayerAvatar.fragments.anyPlayer}
    ${GamesStanding.fragments.TeamInterface}
    ${DomesticLeagueRanking.fragments.teamInterface}
  ` as TypedDocumentNode<GameTeamsMatchup_anyGameInterface>,
};
